import React from "react";

import { RotateCw } from "lucide-react";

import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import {
  useCrawlerPreferencesState,
  useCrawlerPreferenceTriggerRefetch,
  useFetchCrawlerPreference,
  useGetLatestAccountId,
  useUpdateCrawlerPreference,
} from "../hooks";

import { AccountInfoCard } from "./shared/AccountInfoCard";
import { ActionButton } from "./shared/ActionButton";
import { ErrorState } from "./shared/ErrorState";
import { LimitationNote } from "./shared/LimitationNote";
import { LoadingState } from "./shared/LoadingState";
import { LongLoadingState } from "./shared/LongLoadingState";
import { StepHeader } from "./shared/StepHeader";
import { STEP_BODY_CLASS, StepLayout } from "./shared/StepLayout";
import { CrawlerPreferenceSelector } from "./CrawlerPreferenceSelector";
import { StepIndicator } from "./StepIndicator";

import type { CrawlerPreferenceData } from "../types/crawlerPreference";
import type { ModalStepProps } from "../types/modal";

interface CrawlerPreferenceStepContentProps extends ModalStepProps {
  onNext?: () => void;
  onError?: (error?: string) => void;
  crawlerPreferenceData: CrawlerPreferenceData;
  accountIdToUse: string;
}

function CrawlerPreferenceStepContent({
  integration,
  onNext,
  onError,
  isLoading,
  crawlerPreferenceData,
  accountIdToUse,
}: CrawlerPreferenceStepContentProps) {
  const preferencesState = useCrawlerPreferencesState({
    crawlerPreferenceData,
  });

  const updatePreferenceMutation = useUpdateCrawlerPreference({
    onSuccess: () => {
      onNext?.();
    },
    onError: (error) => {
      onError?.(error.message || "Failed to update preferences");
    },
  });

  const triggerRefetchMutation = useCrawlerPreferenceTriggerRefetch({
    onSuccess: () => {
      // The query will be automatically invalidated and refetched by the hook
    },
    onError: (error) => {
      onError?.(error.message || "Failed to refresh preferences");
    },
  });

  const handleRefresh = () => {
    if (!accountIdToUse) {
      onError?.("No account selected for refresh");
      return;
    }

    triggerRefetchMutation.mutate({
      source: integration.label,
      verifiableId: accountIdToUse,
    });
  };

  const handleSync = () => {
    if (!crawlerPreferenceData || !accountIdToUse) {
      onError?.("Missing required data for sync");
      return;
    }

    const updatedData = {
      ...crawlerPreferenceData,
      preferences: preferencesState.preferencesForSave,
    };

    updatePreferenceMutation.mutate({
      source: integration.label,
      verifiableId: accountIdToUse,
      data: updatedData,
    });
  };

  return (
    <StepLayout
      bodyClassName="pt-6"
      footer={
        <ActionButton
          className="w-full h-10"
          onClick={handleSync}
          disabled={
            isLoading ||
            updatePreferenceMutation.isPending ||
            triggerRefetchMutation.isPending
          }
          loading={updatePreferenceMutation.isPending}
          loadingText="Syncing..."
          icon={<RotateCw className="w-4 h-4" />}
        >
          Sync
        </ActionButton>
      }
    >
      <div className="flex flex-col gap-6">
        <AccountInfoCard
          title="Connected as"
          accountId={
            accountIdToUse ||
            crawlerPreferenceData?.name ||
            crawlerPreferenceData?.verifiable_id ||
            ""
          }
        />
        <LimitationNote sourceId={integration.label} />
        <CrawlerPreferenceSelector
          source={integration.label}
          state={preferencesState}
          onRefresh={handleRefresh}
          isRefreshing={triggerRefetchMutation.isPending}
        />
      </div>
    </StepLayout>
  );
}

// New wrapper component that handles loading states and WAIT responses
export function CrawlerPreferenceStep({
  integration,
  onNext,
  onError,
  onClose,
  isLoading,
  selectedAccountId,
}: ModalStepProps & {
  onError?: (error?: string) => void;
}) {
  const { accountIdToUse, isFetchingAccounts } = useGetLatestAccountId({
    sourceId: integration.label,
    selectedAccountId,
  });

  const {
    data: crawlerPreferenceData,
    isLoading: isFetchingPreferences,
    error: fetchError,
  } = useFetchCrawlerPreference(integration.label, accountIdToUse, {
    enabled: !!accountIdToUse,
  });

  React.useEffect(() => {
    if (
      crawlerPreferenceData &&
      "process" in crawlerPreferenceData &&
      crawlerPreferenceData.process === "ERROR"
    ) {
      console.error(
        "Crawler preference error:",
        crawlerPreferenceData.error_message,
      );
    }
  }, [crawlerPreferenceData]);

  return (
    <>
      <div className="flex-shrink-0">
        <StepHeader integration={integration} />
        <StepIndicator
          currentStep="crawler_preference"
          sourceId={integration.label}
        />
      </div>
      {/* 1. API is loading */}
      {(isFetchingPreferences || isFetchingAccounts) && (
        <div className={cn(STEP_BODY_CLASS, "pt-6")}>
          <div className="flex items-center justify-center py-8">
            <LoadingState message="Loading preferences..." />
          </div>
        </div>
      )}

      {/* 2. API response is present but got process: 'WAIT' */}
      {!isFetchingPreferences &&
        !isFetchingAccounts &&
        crawlerPreferenceData &&
        "process" in crawlerPreferenceData &&
        crawlerPreferenceData.process === "WAIT" && (
          <div className={cn(STEP_BODY_CLASS, "pt-6")}>
            <LongLoadingState
              sourceId={integration.label}
              sourceLabel={integration.value}
            />
          </div>
        )}

      {/* 3. Error state */}
      {!isFetchingPreferences &&
        !isFetchingAccounts &&
        (fetchError ||
          (crawlerPreferenceData &&
            "process" in crawlerPreferenceData &&
            crawlerPreferenceData.process === "ERROR")) && (
          <div className={cn(STEP_BODY_CLASS, "pt-6 pb-2 text-center")}>
            <ErrorState
              title="Failed to load preferences"
              message={
                crawlerPreferenceData &&
                "error_message" in crawlerPreferenceData
                  ? crawlerPreferenceData.error_message
                  : undefined
              }
              className="bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800 rounded-lg p-6 border mb-4"
            />
            <Button variant="outline" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        )}

      {/* 4. API response is present and got actual response with preferences */}
      {!isFetchingPreferences &&
        !isFetchingAccounts &&
        !fetchError &&
        crawlerPreferenceData &&
        "preferences" in crawlerPreferenceData &&
        accountIdToUse && (
          <CrawlerPreferenceStepContent
            integration={integration}
            onNext={onNext}
            onError={onError}
            onClose={onClose}
            isLoading={isLoading}
            selectedAccountId={selectedAccountId}
            crawlerPreferenceData={crawlerPreferenceData}
            accountIdToUse={accountIdToUse}
          />
        )}
    </>
  );
}
